import { Button, CircularProgress, styled } from "@mui/material";
import { ChangeEventHandler, useState } from "react";
import { readAndCompressImage } from "browser-image-resizer";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { graphql } from "@/config/graphql";
import { toast } from "react-toastify";
const PreviewImage = styled("img")({
  width: "100%",
  height: "auto",
  objectFit: "contain",
});
const PreviewImageContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "auto",
  objectFit: "contain",
  padding: "16px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  backgroundColor: "#f9f9f9",
  position: "relative",
});
const LoaderContainer = styled("div")({
  position: "absolute",
  width: "100%",
  height: "100%",
  background: "rgba(0, 0, 0, 0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
const Loader = styled(CircularProgress)({});

const getPhotoUploadLink = graphql(
  `
    mutation getPhotoUploadLink($params: GetPhotoUploadLinkParams!) {
      getPhotoUploadLink(params: $params) {
        url
        fields
      }
    }
  `
);

interface UploadButtonProps {
  children?: React.ReactNode;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  error?: boolean;
}
export const UploadButton = (props: UploadButtonProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [getPhotoUploadLinkMutate] = useMutation(getPhotoUploadLink);
  const handleChange: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!event.target.files) return;
    props.onChange(undefined);
    const inputFile = event.target.files[0];
    const url = await readFile(inputFile);
    setPreviewUrl(url);
    setUploading(true);
    try {
      const [compressed, { data }] = await Promise.all([
        readAndCompressImage(inputFile, {
          maxHeight: 600,
          maxWidth: 600,
          quality: 0.8,
        }),
        getPhotoUploadLinkMutate({
          variables: {
            params: {
              fileKey: inputFile.name,
            },
          },
        }),
      ]);
      const { url, fields } = data?.getPhotoUploadLink ?? {
        url: "",
        fields: {},
      };
      const {
        success,
        url: storageUrl,
        error: uploadError,
      } = await uploadFileWithPresignedPost(
        compressed,
        {
          name: inputFile.name,
        },
        { url, fields: fields as Record<string, string> }
      );
      if (!success) {
        toast.error(
          "Hubo un error al subir la imágen. Intente de nuevo o contacte al soporte."
        );
        throw new Error(uploadError);
      }

      props.onChange(storageUrl);
      setPreviewUrl(null);
    } catch (e) {
      console.log(e);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };
  const showUrl = props.value || previewUrl;
  return (
    <>
      {showUrl ? (
        <PreviewImageContainer>
          <PreviewImage src={showUrl} alt="Preview" />
          {uploading && (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          )}
          <Button
            variant="contained"
            sx={{
              borderRadius: "50%",
              padding: 0,
              width: 32,
              height: 32,
              minWidth: 32,
              position: "absolute",
              top: 8,
              right: 8,
            }}
            onClick={() => props.onChange("")}
          >
            <CloseIcon />
          </Button>
        </PreviewImageContainer>
      ) : (
        <Button
          variant="contained"
          component="label"
          sx={{
            border: props.error ? "1px solid red" : undefined,
          }}
        >
          {props.children}
          <input onChange={handleChange} type="file" hidden multiple={false} />
        </Button>
      )}
    </>
  );
};

async function readFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function uploadFileWithPresignedPost(
  file: File | Blob,
  config: {
    name: string;
  },
  presignedData: {
    url: string;
    fields: Record<string, string>;
  }
) {
  const formData = new FormData();

  // Agregar campos firmados al FormData
  Object.entries(presignedData.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("Content-Type", file.type);

  // Agregar el archivo al FormData (debe ser el último campo)
  formData.append("file", file);

  try {
    const response = await fetch(presignedData.url, {
      method: "POST",
      body: formData,
      // ¡No establecer Content-Type! El navegador lo manejará automáticamente
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error en subida: ${errorText}`);
    }

    // Construir URL pública del archivo subido
    const publicUrl = `${presignedData.url}/${presignedData.fields.key.replace(
      "${filename}",
      config.name
    )}`;

    return {
      success: true,
      url: publicUrl,
      fileName: config.name,
    };
  } catch (error) {
    console.error("Error subiendo archivo:", error);
    if (error instanceof Error) {
      return {
        success: false,
        error: error.message,
      };
    }
    return {
      success: false,
      error: "Error desconocido",
    };
  }
}
